import { useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  enableWebPush,
  isIosDevice,
  isStandaloneDisplay,
  pushOptInCanAllow,
} from "../../lib/push-client.ts";
import {
  dismissPushOptInToast,
  EDM_PUSH_EVENT,
  isPushOptInToastDismissed,
  pushOptInCopy,
  syncPushOptInVisibility,
  watchPushOptInVisibility,
} from "../../lib/push-optin.ts";

function showOnPath(pathname: string) {
  return pathname.startsWith("/app") || pathname.startsWith("/account");
}

/**
 * One-tap subscribe on the workspace and account pages. iOS Safari that is
 * not on the Home Screen gets the install copy instead of Allow.
 */
export function PushOptIn({ signedIn }: { signedIn: boolean }) {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [homeScreen, setHomeScreen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    function refresh() {
      if (!signedIn || !showOnPath(pathname) || !("Notification" in window)) {
        setVisible(false);
        return;
      }
      const granted = syncPushOptInVisibility();
      if (cancelled) return;
      if (granted || isPushOptInToastDismissed()) {
        setVisible(false);
        return;
      }
      setHomeScreen(isIosDevice() && !isStandaloneDisplay());
      setVisible(true);
    }
    const stop = watchPushOptInVisibility(refresh);
    return () => {
      cancelled = true;
      stop();
    };
  }, [pathname, signedIn]);

  if (!visible) return null;

  async function allow() {
    setBusy(true);
    setError(null);
    try {
      await enableWebPush();
      setVisible(false);
      window.dispatchEvent(new Event(EDM_PUSH_EVENT));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn’t turn alerts on. Check this site’s notification setting and try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    dismissPushOptInToast();
    setVisible(false);
    window.dispatchEvent(new Event(EDM_PUSH_EVENT));
  }

  const { title, body } = pushOptInCopy({ homeScreen });

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
      <div className="pointer-events-auto w-[min(24rem,calc(100vw-1.5rem))] rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="mt-1 text-sm leading-relaxed text-gray-500">{body}</p>
        {error ? <p className="mt-2 text-sm leading-relaxed text-gray-500">{error}</p> : null}
        <div className="mt-3 flex flex-wrap gap-2">
          {pushOptInCanAllow(homeScreen) ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void allow()}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              Allow
            </button>
          ) : null}
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
